import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Deposant, Justificatif } from '@troc/shared'
import { eur, tauxEnPourcent } from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { PastilleReversement } from '../composants/Pastille'
import { imprimerJustificatif } from '../impression/documents'
import { useToast } from '../etat/Toast'

interface LigneARemettre {
  id: number
  ref: string
  libelle: string
  date: string
  prixCentimes: number
  netCentimes: number
  taux: number
}

interface Apercu {
  lignes: LigneARemettre[]
  montantCentimes: number
  mode: 'especes' | 'virement'
}

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' })

/**
 * Le geste de paiement, en trois temps : voir ce qu'on va remettre, l'enregistrer,
 * puis imprimer le justificatif — jamais les trois d'un coup.
 *
 * Avant cet écran, cliquer « Remettre » enregistrait le versement et lançait
 * l'impression dans le même geste : personne n'avait l'occasion de vérifier ce
 * qui allait être payé avant que ce soit fait. Ici, rien n'est écrit tant que
 * « Enregistrer le paiement » n'a pas été touché.
 */
export function Remettre() {
  const { id } = useParams()
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [deposant, setDeposant] = useState<Deposant | null>(null)
  const [apercu, setApercu] = useState<Apercu | null>(null)
  const [anticipe, setAnticipe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const [justificatif, setJustificatif] = useState<Justificatif | null>(null)
  const [annulation, setAnnulation] = useState(false)

  const charger = useCallback(() => {
    if (!id) return
    api<{ deposant: Deposant }>(`/deposants/${id}`)
      .then((r) => setDeposant(r.deposant))
      .catch(() => undefined)
    api<Apercu>(`/deposants/${id}/a-remettre${anticipe ? '?anticipe=1' : ''}`)
      .then(setApercu)
      .catch((cause: unknown) =>
        setErreur(cause instanceof ErreurApi ? cause.message : 'Impossible de charger le solde.'),
      )
  }, [id, anticipe])

  useEffect(charger, [charger])

  const enregistrer = async () => {
    if (!id || envoi || !apercu || apercu.montantCentimes <= 0) return
    setEnvoi(true)
    try {
      const reponse = await api<{ justificatif: Justificatif }>(`/deposants/${id}/reverser`, {
        methode: 'POST',
        corps: { inclureNonEchu: anticipe },
      })
      setJustificatif(reponse.justificatif)
      signaler(`Versement de ${eur(reponse.justificatif.montantCentimes)} enregistré.`)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  /**
   * Un justificatif ne s'annule que dans les dix minutes — voir le garde-fou
   * côté serveur. Passé ce délai, la reprise se fait à la main.
   */
  const annuler = async () => {
    if (!justificatif || annulation) return
    setAnnulation(true)
    try {
      await api(`/justificatifs/${justificatif.id}`, { methode: 'DELETE' })
      signaler(`Justificatif ${justificatif.num} annulé.`)
      setJustificatif(null)
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Annulation impossible.')
    } finally {
      setAnnulation(false)
    }
  }

  if (erreur) {
    return (
      <Ecran titre="Remettre" sousTitre="Introuvable">
        <div className="vide">{erreur}</div>
      </Ecran>
    )
  }

  if (!deposant || !apercu) {
    return (
      <Ecran titre="Remettre" sousTitre="Chargement…">
        <div className="vide">Chargement…</div>
      </Ecran>
    )
  }

  return (
    <Ecran
      titre={justificatif ? 'Paiement enregistré' : 'Remettre au déposant'}
      sousTitre={`${deposant.nom} · ${deposant.ref}`}
      outils={
        <Link to={`/deposants/${deposant.id}`} className="btn btn-secondaire">
          ← Retour à la fiche
        </Link>
      }
    >
      <div className="zone-defilante" style={{ maxWidth: 900 }}>
        {justificatif ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="bandeau-kpi" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="kpi kpi-vedette">
                <div className="etiquette">Payé</div>
                <div className="valeur">{eur(justificatif.montantCentimes)}</div>
                <div className="detail">
                  Justificatif {justificatif.num} · <PastilleReversement mode={justificatif.mode} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                onClick={() => imprimerJustificatif(justificatif)}
              >
                Imprimer le justificatif
              </button>
              <button
                type="button"
                className="btn btn-secondaire btn-large"
                onClick={() => naviguer(`/deposants/${deposant.id}`)}
              >
                Retour à la fiche
              </button>
              <button
                type="button"
                className="btn btn-fantome"
                disabled={annulation}
                onClick={() => {
                  if (window.confirm(`Annuler le justificatif ${justificatif.num} ?`)) void annuler()
                }}
              >
                {annulation ? 'Annulation…' : 'Ce n’était pas voulu — annuler ce justificatif'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="bandeau-kpi" style={{ gridTemplateColumns: '1fr' }}>
              <div className="kpi kpi-vedette">
                <div className="etiquette">
                  {anticipe ? 'Total à remettre, avance comprise' : 'À remettre aujourd’hui'}
                </div>
                <div className="valeur">{eur(apercu.montantCentimes)}</div>
                <div className="detail">
                  {apercu.lignes.length} vente{apercu.lignes.length > 1 ? 's' : ''} ·{' '}
                  <PastilleReversement mode={apercu.mode} />
                </div>
              </div>
            </div>

            <label className="case-a-cocher" style={{ margin: '14px 0' }}>
              <input
                type="checkbox"
                checked={anticipe}
                onChange={(e) => setAnticipe(e.target.checked)}
              />
              <span>
                Payer d’avance
                <span className="sourdine">
                  {' '}
                  — inclut aussi les ventes pas encore échues
                </span>
              </span>
            </label>

            <table className="tableau">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Réf.</th>
                  <th>Désignation</th>
                  <th className="a-droite">Px. vente</th>
                  <th className="a-droite">Commission</th>
                  <th className="a-droite">Net déposant</th>
                </tr>
              </thead>
              <tbody>
                {apercu.lignes.map((ligne) => (
                  <tr key={ligne.id}>
                    <td>{FORMAT_DATE.format(new Date(ligne.date))}</td>
                    <td className="ref">{ligne.ref}</td>
                    <td className="titre">{ligne.libelle}</td>
                    <td className="a-droite chiffre">{eur(ligne.prixCentimes)}</td>
                    <td className="a-droite sourdine">
                      {tauxEnPourcent(ligne.taux)} · {eur(ligne.prixCentimes - ligne.netCentimes)}
                    </td>
                    <td className="a-droite chiffre">{eur(ligne.netCentimes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {apercu.lignes.length === 0 && (
              <div className="vide">Rien à remettre pour le moment.</div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                disabled={envoi || apercu.montantCentimes <= 0}
                onClick={() => void enregistrer()}
              >
                Enregistrer le paiement — {eur(apercu.montantCentimes)}
              </button>
              <button
                type="button"
                className="btn btn-secondaire btn-large"
                onClick={() => naviguer(`/deposants/${deposant.id}`)}
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </Ecran>
  )
}
